import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, Video, Upload, CheckCircle2, XCircle, Loader2, AlertCircle, RotateCcw, Lock } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SpeedIQAnalysisRunner from "@/components/SpeedIQAnalysisRunner";
import SpeedIQRearAnalysisRunner from "@/components/SpeedIQRearAnalysisRunner";
import SpeedIQResults from "@/components/SpeedIQResults";
import ZybekSplitEntry from "@/components/ZybekSplitEntry";

/**
 * SpeedIQPage — P0 capture wizard.
 *
 * Per the SpeedIQ blueprint:
 *   • Two sport modes (baseball: 10y/30y6/60y · other_sports: 10y/20y/40y)
 *   • MANDATORY dual-view capture (side + rear) — Analyze button LOCKED
 *     until both clips pass server-side validation
 *
 * This page is the first surface of the SpeedIQ MVP. The P1 pose pipeline,
 * P3 Zybek split entry, and the P4 scoring engine ship in subsequent
 * sprints. The shape of this wizard is final; only the downstream analysis
 * step changes.
 */
const TEST_TYPES_BY_MODE = {
  baseball: [
    { v: "10y",  label: "10-yard burst" },
    { v: "30y6", label: "30.6-Yard Split — Home-to-First" },
    { v: "60y",  label: "60-yard (scout split)" },
    { v: "first_step", label: "First-step assessment" },
  ],
  other_sports: [
    { v: "10y", label: "10-yard burst" },
    { v: "20y", label: "20-yard sprint" },
    { v: "40y", label: "40-yard sprint" },
    { v: "first_step", label: "First-step assessment" },
  ],
};

const CONTEXTS = [
  { v: "assessment", label: "Assessment" },
  { v: "training",   label: "Training" },
  { v: "baseline",   label: "Baseline" },
  { v: "progress",   label: "Progress check" },
  { v: "rtp",        label: "Return to play" },
];

export default function SpeedIQPage() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const athleteParam = params.get("athleteId") || null;

  const [sportMode, setSportMode] = useState("baseball");
  const [testType, setTestType] = useState("30y6");
  const [context, setContext] = useState("assessment");
  const [session, setSession] = useState(null);
  const [analyzeResponse, setAnalyzeResponse] = useState(null);
  const [creating, setCreating] = useState(false);

  const sideInputRef = useRef(null);
  const rearInputRef = useRef(null);

  // ── Create session ──────────────────────────────────────────────
  async function createSession() {
    setCreating(true);
    try {
      const payload = {
        sport_mode: sportMode,
        test_type: testType,
        session_context: context,
      };
      if (athleteParam) payload.athlete_user_id = athleteParam;
      const { data } = await api.post("/speediq/sessions", payload);
      setSession(data);
      toast.success(`Session created · ${data.test_type.toUpperCase()}`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not start a SpeedIQ session.");
    } finally {
      setCreating(false);
    }
  }

  async function uploadView(view, fileList) {
    const file = fileList?.[0];
    if (!file) return;
    // Cache the blob so the analysis runner can re-decode it client-side.
    window.__lastSpeedIQClip = window.__lastSpeedIQClip || {};
    window.__lastSpeedIQClip[view] = file;
    const fd = new FormData();
    fd.append("file", file);
    // Best-effort metadata pulled from a <video> probe before upload.
    try {
      const meta = await probeVideo(file);
      if (meta.duration_sec != null) fd.append("duration_sec", String(meta.duration_sec));
      if (meta.width) fd.append("width", String(meta.width));
      if (meta.height) fd.append("height", String(meta.height));
    } catch {/* probe is optional */}
    try {
      const { data } = await api.post(`/speediq/sessions/${session.session_id}/upload/${view}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSession(data);
      const view_status = view === "side" ? data.validation.side_view : data.validation.rear_view;
      if (view_status === "complete") toast.success(`${view === "side" ? "Side" : "Rear"} view captured.`);
      else if (view_status === "failed") toast.error(`${view === "side" ? "Side" : "Rear"} view failed validation — re-record.`);
    } catch (e) {
      console.error(e);
      toast.error("Upload failed — check the clip and try again.");
    }
  }

  async function clearView(view) {
    try {
      const { data } = await api.delete(`/speediq/sessions/${session.session_id}/upload/${view}`);
      setSession(data);
    } catch (e) {
      toast.error("Could not clear the clip.");
    }
  }

  async function analyze() {
    try {
      const { data } = await api.post(`/speediq/sessions/${session.session_id}/analyze`);
      setAnalyzeResponse(data);
      const overall = data.scores?.overall?.score;
      toast.success(`SpeedIQ ${data.phase} · overall ${overall ?? "—"}/100`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "dual_view_gate") {
        toast.error("Dual-view gate locked — both clips must pass validation.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not start analysis.");
      }
    }
  }

  const v = session?.validation;
  const analyzeUnlocked = !!v?.analyze_unlocked;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--ink-1)" }} data-testid="speediq-page">
      <div className="max-w-3xl mx-auto px-6 pt-10 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Logo size="sm" variant="lockup" linkTo="/" dark />
          <span className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-bold">SpeedIQ</span>
        </div>
        <Link to={user?.role === "coach" || user?.role === "admin" ? "/dashboard" : "/portal"}
              className="text-xs text-white/55 hover:text-white inline-flex items-center gap-1" data-testid="speediq-back">
          <ChevronLeft className="h-3.5 w-3.5" /> Back
        </Link>
      </div>

      <div className="max-w-3xl mx-auto px-6 pb-16 space-y-6">
        {/* HERO */}
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.25em] uppercase border"
               style={{ backgroundColor: "rgba(217,4,41,0.12)", borderColor: "rgba(217,4,41,0.35)", color: "var(--brand-secondary)" }}>
            <Video className="h-3 w-3" /> Sprint Mechanics · Dual-View Capture
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-white mt-3 tracking-tighter">
            How fast — <span style={{ color: "var(--brand-secondary)" }}>and why.</span>
          </h1>
          <p className="text-sm text-white/65 mt-3 leading-relaxed">
            SpeedIQ captures sprint mechanics from your phone with mandatory <b className="text-white">side</b> + <b className="text-white">rear</b> video. Pair with Zybek splits and the system explains not just how fast the athlete moved — but the movement causes behind it.
          </p>
        </div>

        {/* STEP 1 · Session setup */}
        {!session && (
          <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-6 space-y-5" data-testid="speediq-setup">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)]">Step 1 · Session setup</div>

            {/* Sport mode */}
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-white/55 mb-2">Sport mode</div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { v: "baseball",     title: "Baseball",     sub: "10-yd · 30.6-yd Home-to-First · 60-yd" },
                  { v: "other_sports", title: "Other sports", sub: "10-yd · 20-yd · 40-yd" },
                ].map((m) => (
                  <button key={m.v} onClick={() => {
                            setSportMode(m.v);
                            setTestType(m.v === "baseball" ? "30y6" : "40y");
                          }}
                          data-testid={`speediq-sport-${m.v}`}
                          className={`rounded-lg p-4 border text-left transition-colors
                                      ${sportMode === m.v
                                        ? "border-[var(--brand-secondary)] bg-[var(--brand-secondary)]/10"
                                        : "border-white/10 bg-black/30 hover:border-white/30"}`}>
                    <div className="text-white font-bold">{m.title}</div>
                    <div className="text-[10px] text-white/55 mt-1 uppercase tracking-wide">{m.sub}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Test type */}
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-white/55 mb-2">Test type</div>
              <div className="flex flex-wrap gap-2">
                {TEST_TYPES_BY_MODE[sportMode].map((t) => (
                  <button key={t.v} onClick={() => setTestType(t.v)} data-testid={`speediq-test-${t.v}`}
                          className={`px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border
                                      ${testType === t.v
                                        ? "border-[var(--brand-secondary)] bg-[var(--brand-secondary)]/15 text-white"
                                        : "border-white/15 bg-black/30 text-white/65 hover:text-white"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Context */}
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-white/55 mb-2">Session context</div>
              <div className="flex flex-wrap gap-2">
                {CONTEXTS.map((c) => (
                  <button key={c.v} onClick={() => setContext(c.v)} data-testid={`speediq-ctx-${c.v}`}
                          className={`px-3 py-1.5 rounded text-[11px] font-bold uppercase tracking-wider border
                                      ${context === c.v
                                        ? "border-[var(--brand-secondary)] bg-[var(--brand-secondary)]/15 text-white"
                                        : "border-white/15 bg-black/30 text-white/55 hover:text-white"}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <Button onClick={createSession} disabled={creating} className="w-full"
                    style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="speediq-create">
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Video className="h-4 w-4 mr-2" />}
              Start session
            </Button>
          </Card>
        )}

        {/* STEP 2 · Dual-view capture */}
        {session && (
          <>
            <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-5" data-testid="speediq-capture">
              <div className="flex items-baseline justify-between mb-3">
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)]">Step 2 · Dual-view capture</div>
                  <div className="text-base font-display font-black text-white mt-0.5">
                    {session.sport_mode === "baseball" ? "Baseball" : "Other sports"} · {session.test_type.toUpperCase()}
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/40">
                  Session {session.session_id.slice(-6)}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <ViewSlot
                  view="side"
                  status={v.side_view}
                  asset={session.side_view}
                  onFile={(fs) => uploadView("side", fs)}
                  onClear={() => clearView("side")}
                  inputRef={sideInputRef}
                  hint="Side view · ~3-4m from sprint lane, hip height, landscape, 5-10s of full body"
                />
                <ViewSlot
                  view="rear"
                  status={v.rear_view}
                  asset={session.rear_view}
                  onFile={(fs) => uploadView("rear", fs)}
                  onClear={() => clearView("rear")}
                  inputRef={rearInputRef}
                  hint="Rear view · directly behind, ~2-3m from start, landscape, both lower-extremities + pelvis visible"
                />
              </div>

              {/* Validation gate banner */}
              <div className={`mt-4 rounded-lg p-3 border ${
                analyzeUnlocked
                  ? "border-emerald-500/40 bg-emerald-500/5"
                  : "border-amber-500/30 bg-amber-500/5"
              }`} data-testid="speediq-gate">
                <div className="flex items-center gap-2 text-xs">
                  {analyzeUnlocked
                    ? <CheckCircle2 className="h-4 w-4 text-emerald-300" />
                    : <Lock className="h-4 w-4 text-amber-300" />}
                  <div className="font-bold text-white text-sm">
                    {analyzeUnlocked
                      ? "Dual-view gate unlocked — analysis ready."
                      : "Dual-view gate locked — both clips must pass validation."}
                  </div>
                </div>
                {v.reasons?.length > 0 && (
                  <ul className="mt-2 ml-5 text-[11px] text-amber-100 list-disc">
                    {v.reasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                )}
              </div>

              <Button onClick={analyze} disabled={!analyzeUnlocked}
                      className="w-full mt-4" style={{ backgroundColor: "var(--brand-secondary)" }}
                      data-testid="speediq-analyze">
                {analyzeUnlocked ? "▶ Run SpeedIQ analysis" : <><Lock className="h-4 w-4 mr-2" /> Both clips required</>}
              </Button>
            </Card>

            {/* Side-view P1 analysis */}
            {session.side_view && !session.side_view.rejected_reason && (
              <SpeedIQAnalysisRunner
                sessionId={session.session_id}
                sideAsset={session.side_view}
                onComplete={() => { /* metrics already in DB; UI refreshes via /analyze */ }}
              />
            )}

            {/* Rear-view P2 analysis */}
            {session.rear_view && !session.rear_view.rejected_reason && (
              <SpeedIQRearAnalysisRunner
                sessionId={session.session_id}
                rearAsset={session.rear_view}
                onComplete={() => { /* metrics already in DB; UI refreshes via /analyze */ }}
              />
            )}

            {/* Zybek splits — sport-mode-aware */}
            <ZybekSplitEntry
              sessionId={session.session_id}
              sportMode={session.sport_mode}
              existingRollup={session.splits_rollup}
              onSaved={(rollup) => setSession((s) => ({ ...s, splits_rollup: rollup }))}
            />

            {/* P5 Results page — renders after /analyze runs */}
            {analyzeResponse && <SpeedIQResults analyzeResponse={analyzeResponse} />}

            <div className="text-[11px] text-white/40 text-center">
              <b className="text-white/65">SpeedIQ P2 + P4 + P5 + P6 live.</b> Athlete report PDF (P7) + public share artifact (P8) ship in Wave 3 per <code className="bg-black/30 px-1.5 py-0.5 rounded">SPEEDIQ_BLUEPRINT.md</code>.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────
function ViewSlot({ view, status, asset, onFile, onClear, inputRef, hint }) {
  return (
    <div className={`rounded-xl border p-4 transition-colors
                     ${status === "complete" ? "border-emerald-500/40 bg-emerald-500/5"
                       : status === "failed" ? "border-amber-500/40 bg-amber-500/5"
                       : "border-white/10 bg-black/30"}`}
         data-testid={`speediq-slot-${view}`}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white">
          {view === "side" ? "Side view" : "Rear view"}
        </div>
        <StatusPill status={status} />
      </div>
      <p className="text-[11px] text-white/55 leading-snug mb-3">{hint}</p>

      {asset?.filename ? (
        <div className="text-xs text-white/70 space-y-1">
          <div className="truncate font-mono">{asset.filename}</div>
          <div className="text-[10px] text-white/45">
            {((asset.bytes || 0) / (1024 * 1024)).toFixed(1)} MB
            {asset.duration_sec != null && <> · {asset.duration_sec.toFixed(1)}s</>}
            {asset.width && asset.height && <> · {asset.width}×{asset.height}</>}
          </div>
          {asset.rejected_reason && (
            <div className="text-[11px] text-amber-200 mt-2 flex items-start gap-1.5">
              <AlertCircle className="h-3 w-3 mt-0.5 flex-none" />
              <span>{asset.rejected_reason}</span>
            </div>
          )}
          <Button onClick={onClear} variant="outline" size="sm"
                  className="mt-3 w-full border-white/15 text-white/65"
                  data-testid={`speediq-clear-${view}`}>
            <RotateCcw className="h-3 w-3 mr-1.5" /> Re-record
          </Button>
        </div>
      ) : (
        <>
          <input ref={inputRef} type="file" accept="video/*"
                 onChange={(e) => onFile(e.target.files)} className="hidden"
                 data-testid={`speediq-file-${view}`} />
          <Button onClick={() => inputRef.current?.click()} variant="outline"
                  className="w-full border-[var(--brand-secondary)]/40 text-white bg-[var(--brand-secondary)]/10 hover:bg-[var(--brand-secondary)]/20"
                  data-testid={`speediq-upload-${view}`}>
            <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload {view} clip
          </Button>
        </>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    complete: { Icon: CheckCircle2, c: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
    failed:   { Icon: XCircle,      c: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
    missing:  { Icon: Lock,         c: "text-white/45 border-white/15 bg-black/30" },
  };
  const { Icon, c } = map[status] || map.missing;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.2em] font-bold border ${c}`}
          data-testid={`speediq-status-${status}`}>
      <Icon className="h-3 w-3" />{status}
    </span>
  );
}

// Probe a video file for duration + dimensions client-side. Never throws —
// returns nulls if metadata can't be read.
function probeVideo(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve({
        duration_sec: Number.isFinite(v.duration) ? +v.duration.toFixed(2) : null,
        width: v.videoWidth || null,
        height: v.videoHeight || null,
      });
    };
    v.onerror = () => { URL.revokeObjectURL(url); resolve({ duration_sec: null, width: null, height: null }); };
    v.src = url;
  });
}
